
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Button from '../components/Button';

interface Student {
    id: string;
    name: string;
    email: string;
    points: number;
    grade: number;
}

interface Assignment {
    id: string;
    title: string;
    subject: string;
    due_date: string;
}

interface ClassDetails {
    id: string;
    name: string;
    grade: number;
    subject: string;
    class_code: string;
    students: Student[];
    assignments: Assignment[];
}

const ClassDetailsPage: React.FC = () => {
    const { classId } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [classData, setClassData] = useState<ClassDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const data = await api.get(`/teacher/classes/${classId}`);
                setClassData(data);
            } catch (err) {
                setError('Failed to load class details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (classId) fetchDetails();
    }, [classId]);

    const [showCreateAssignment, setShowCreateAssignment] = useState(false);
    const [newAssignment, setNewAssignment] = useState({
        title: '',
        subject: 'Math',
        topic: '',
        due_date: ''
    });

    const handleCreateAssignment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!classData) return;

        try {
            await api.post(`/teacher/classes/${classData.id}/assignments`, newAssignment);
            // Refresh data
            const data = await api.get(`/teacher/classes/${classId}`);
            setClassData(data);
            setShowCreateAssignment(false);
            setNewAssignment({ title: '', subject: 'Math', topic: '', due_date: '' });
        } catch (err) {
            alert('Failed to create assignment');
        }
    };

    const handleRemoveStudent = async (studentId: string, studentName: string) => {
        if (!confirm(`Are you sure you want to remove ${studentName} from this class?`)) return;

        try {
            await api.delete(`/teacher/classes/${classId}/students/${studentId}`);
            // Optimistic update
            if (classData) {
                setClassData({
                    ...classData,
                    students: classData.students.filter(s => s.id !== studentId)
                });
            }
        } catch (err) {
            alert('Failed to remove student');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading class details...</div>;
    if (error || !classData) return <div className="p-8 text-center text-red-500">{error || 'Class not found'}</div>;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <Button variant="secondary" onClick={() => navigate('/teacher')} className="mb-6">
                <i className="fas fa-arrow-left mr-2"></i> Back to Dashboard
            </Button>

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-slate-100">
                <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-8 text-white">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold mb-2">{classData.name}</h1>
                            <div className="flex items-center gap-4 text-teal-100">
                                <span><i className="fas fa-graduation-cap mr-1"></i> Grade {classData.grade}</span>
                                <span><i className="fas fa-book mr-1"></i> {classData.subject}</span>
                            </div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 text-center">
                            <div className="text-xs uppercase font-bold tracking-wider mb-1 text-teal-100">Class Code</div>
                            <div className="text-2xl font-mono font-bold tracking-widest">{classData.class_code}</div>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Students Roster */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-slate-800">Students ({classData.students.length})</h2>
                                <Button variant="outline" size="sm" onClick={() => alert('Invite feature coming soon!')}>
                                    <i className="fas fa-user-plus mr-2"></i> Invite
                                </Button>
                            </div>

                            {classData.students.length === 0 ? (
                                <div className="bg-slate-50 rounded-xl p-6 text-center text-slate-500 border border-slate-100">
                                    <p>No students have joined yet.</p>
                                    <p className="text-sm mt-2">Share the code <strong>{classData.class_code}</strong> with your students.</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold">
                                            <tr>
                                                <th className="px-4 py-3">Name</th>
                                                <th className="px-4 py-3">Points</th>
                                                <th className="px-4 py-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {classData.students.map(student => (
                                                <tr key={student.id} className="hover:bg-slate-50">
                                                    <td className="px-4 py-3 font-medium text-slate-700">{student.name}</td>
                                                    <td className="px-4 py-3 text-brand-blue font-bold">{student.points}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            onClick={() => handleRemoveStudent(student.id, student.name)}
                                                            className="text-slate-400 hover:text-red-500 text-sm transition-colors"
                                                            title="Remove Student"
                                                        >
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Assignments */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-slate-800">Assignments</h2>
                                <Button size="sm" onClick={() => setShowCreateAssignment(true)}>
                                    <i className="fas fa-plus mr-2"></i> New Assignment
                                </Button>
                            </div>

                            {classData.assignments.length === 0 ? (
                                <div className="bg-slate-50 rounded-xl p-6 text-center text-slate-500 border border-slate-100">
                                    <p>No active assignments.</p>
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {classData.assignments.map(assignment => (
                                        <li key={assignment.id} className="p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-bold text-slate-800">{assignment.title}</h3>
                                                    <p className="text-sm text-slate-500">{assignment.subject} • Due: {new Date(assignment.due_date).toLocaleDateString()}</p>
                                                </div>
                                                <span className="bg-teal-50 text-teal-700 text-xs font-bold px-2 py-1 rounded">Active</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Assignment Modal */}
            {showCreateAssignment && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl animate-fade-in">
                        <h2 className="text-2xl font-bold text-slate-800 mb-6">Create Assignment</h2>
                        <form onSubmit={handleCreateAssignment} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500"
                                    placeholder="e.g. Chapter 4 Review"
                                    value={newAssignment.title}
                                    onChange={e => setNewAssignment({ ...newAssignment, title: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Subject</label>
                                    <select
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500 bg-white"
                                        value={newAssignment.subject}
                                        onChange={e => setNewAssignment({ ...newAssignment, subject: e.target.value })}
                                    >
                                        <option>Math</option>
                                        <option>Science</option>
                                        <option>Language Arts</option>
                                        <option>Social Studies</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Due Date</label>
                                    <input
                                        type="date"
                                        required
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500"
                                        value={newAssignment.due_date}
                                        onChange={e => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Topic (for AI generation)</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none focus:border-teal-500"
                                    placeholder="e.g. Fractions"
                                    value={newAssignment.topic}
                                    onChange={e => setNewAssignment({ ...newAssignment, topic: e.target.value })}
                                />
                                <p className="text-xs text-slate-400 mt-1">Students will get a generic quiz on this topic.</p>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">Assign</Button>
                                <Button variant="outline" type="button" onClick={() => setShowCreateAssignment(false)} className="flex-1">Cancel</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassDetailsPage;
